/* Job detail. Dense on purpose — this is the paper job sheet it replaces.
 *
 * Two states have to work. **Editable**: the signed-in person is on the job (or
 * runs the office). **Read-only**: internal staff who aren't on it can read the
 * whole job but change nothing. Read-only renders values as text with one line
 * saying why, not greyed-out inputs — it should look calm and deliberate, not
 * like a broken form.
 *
 * The screen is the frame, not a page that grows. This is read on an iPad, and
 * the three long lists — items, tasks, photos — stacked ran a couple of screens
 * past the fold, so the job you were looking at scrolled away to reach the
 * tasks on it. They are tabbed instead, and the two columns scroll inside
 * themselves: the header, the stage and the move action never leave the screen. */
import { SHEENS, coatsFor } from '@/data/reference';
import { areaFor, sqmOf } from '@/domain/items';
import { atRisk, historyFor, isOverdue, personFor } from '@/domain/schedule';
import { dayOfInstant, formatDay, formatDayShort, formatInstant } from '@/domain/time';
import type { JobEditField, JobTab } from '@/store/useApp';
import { canEdit, colourFor, currentJob, seesCustomers, stageOf, useApp } from '@/store/useApp';
import { NO_AUTOFILL } from '@/ui/autofill';
import { BackButton, Empty, Panel, PanelHead, Tabs } from '@/ui/controls';
import { useNarrowerThan, usePageGutter, withGutter } from '@/ui/viewport';
import { Avatar, SheenBars, Swatch } from '@/ui/Swatch';
import { NoJobOpen, OverdueBadge } from './JobCard';

/** The tab strip and the list below it are one widget, so the screen names
 *  both halves and each points at the other. */
const JOB_PANEL_ID = 'job-tabpanel';
const jobTabId = (key: string) => `job-tab-${key}`;

/** Where Back goes, named as the place rather than as a direction — it is the
 *  arrow's label, and "Back to Board" is what a tooltip has room to say. */
/* Below this the tab strip's row is drawn without the note beside it.
 *
 * The arithmetic: the left panel takes 1.55 of the 2.55 the two columns share,
 * and the row inside it owes the tab strip and its one action about 430px
 * before the note gets a pixel. Under this the note is what goes — the count is
 * on the tab itself and the m² is on the header above, so nothing said here is
 * said only here. */
const JOB_TAB_NOTE = 940;

const BACK_LABEL: Record<string, string> = {
  board: 'Board',
  jobs: 'Jobs',
  schedule: 'Schedule',
};

export function JobScreen() {
  const gutter = usePageGutter();
  const st = useApp();
  const job = currentJob(st);
  const editable = canEdit(st);
  const roomForTabNote = !useNarrowerThan(JOB_TAB_NOTE);
  // Nothing above this reads the job, and nothing below runs without one.
  if (!job) return <NoJobOpen />;
  const colour = colourFor(st, job.colour);
  // A job can sit at a stage the office has since archived. It still has to
  // render, and it still has to say where it is.
  const stage = stageOf(st, job.stage) ?? { id: job.stage, name: job.stage, tone: '', wip: 0 };
  const { plan } = job;
  const overdue = isOverdue(job, st.stages);
  const risk = atRisk(plan);

  const stageIndex = st.stages.findIndex((s) => s.id === job.stage);
  const nextStage = st.stages[Math.min(stageIndex + 1, st.stages.length - 1)];

  /* The progress track across the top of the job: every stage the office has
     defined, and the one the job is standing at. The stages come from the live
     list, so a stage added or renamed in Settings is a step here on the next
     render. The stage history below gives the day the job reached each one —
     read as a line rather than as a log. */
  const events = historyFor(job, st.stages);
  const onBoard = stageIndex >= 0;
  const track: TrackStep[] = st.stages.map((s, i) => ({
    id: s.id,
    name: s.name,
    tone: s.tone,
    state: !onBoard ? 'ahead' : i < stageIndex ? 'done' : i === stageIndex ? 'now' : 'ahead',
    // "25 Aug 07:40" — the day is what a step has room for.
    when:
      onBoard && i <= stageIndex
        ? events[i]
          ? formatDayShort(dayOfInstant(events[i]!.when))
          : null
        : null,
  }));
  /* A job can sit at a stage the office has since archived, and it still has to
     say where it is: the archived stage leads the track and the board follows
     it, rather than the track quietly reading as not started. */
  if (!onBoard) {
    track.unshift({ id: stage.id, name: stage.name, tone: stage.tone, state: 'now', when: null });
  }

  const itemCount = st.rows.reduce((a, r) => a + r.qty, 0);
  const itemArea = st.rows.reduce((a, r) => a + sqmOf(r), 0);
  const tasksDone = st.tasks.filter((t) => t.done).length;
  const tasksOpen = st.tasks.length - tasksDone;
  const photoCount = job.damaged ? 11 : 6;

  /* What the open tab says about itself, beside the tab strip: the count is on
     the tab, so this is the fact the count doesn't carry. */
  /* One action per tab, in the tab bar where it stays put — a button at the
     foot of a list you have to scroll to is a button nobody presses. */
  const tabAction: Record<JobTab, { label: string; run: () => void }> = {
    items: { label: 'Edit items', run: () => st.set('screen', 'items') },
    tasks: { label: 'Add task', run: () => st.flash(`Task added to ${job.code}.`) },
    photos: {
      label: 'Take photo',
      run: () => st.flash('Camera opens, photo compressed before upload.'),
    },
  };

  const tabMeta: Record<JobTab, string> = {
    items: `${itemArea.toFixed(1)} m²`,
    tasks: tasksOpen ? `${tasksOpen} still to do` : 'All done',
    photos: job.damaged ? `${job.damaged} flagged damage` : 'Goods in, progress, colour match',
  };

  const backTo = st.origin === 'customer' ? st.custName : (BACK_LABEL[st.origin] ?? 'Board');

  /** A header fact's way into the form that types it — and nothing at all for
   *  someone who is not on the job. */
  const edit = (field: JobEditField) => (editable ? () => st.openJobEdit(field) : undefined);

  return (
    <section
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: withGutter(gutter, 16, 24),
      }}
    >
      {/* `relative` is what the fold on the bottom edge hangs off. */}
      <Panel padding={20} style={{ flex: 'none', position: 'relative' }}>
        {/* The code and the track share the top band: the two things someone
            asks of a job sheet — which job, and where it is up to — are read
            on one line, and the track gets the full width of the panel rather
            than the middle column's share of it. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            paddingBottom: 14,
            marginBottom: 18,
            borderBottom: '1px solid var(--line-soft)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
            <BackButton to={backTo} onClick={st.goBack} style={{ marginLeft: -8 }} />
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 20, fontWeight: 500 }}>
                  {job.code}
                </span>
                {overdue ? <OverdueBadge /> : null}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
                <span className="label-xs">Progress</span>
                <span
                  style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: 11.5,
                    color: 'var(--ink-muted)',
                  }}
                >
                  {onBoard ? `Stage ${stageIndex + 1} of ${st.stages.length}` : 'Off the board'}
                </span>
              </div>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <StageTrack
              steps={track}
              note={
                onBoard
                  ? null
                  : `${stage.name} is no longer a stage on the board. Move ${job.code} on to put it back on the run.`
              }
            />
          </div>
        </div>

        {!st.jobHeadOpen ? (
          /* Folded away, the header keeps the three things the lists below it
             cannot answer: what colour this job is, what it is called, and the
             move that takes it on. Everything else is a tab away, and the
             progress track above has not moved. */
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <Swatch code={job.colour} size={34} />
            <div style={{ minWidth: 0 }}>
              <h1
                style={{
                  fontSize: 17,
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {job.title}
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: 12.5,
                  color: 'var(--ink-muted)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {job.customer} · {colour.code} · promised {formatDay(job.promised)}
              </p>
            </div>
            {editable ? (
              <button
                type="button"
                className="btn btn-primary blueprint"
                onClick={() => st.set('moveOpen', true)}
                style={{ height: 48, marginLeft: 'auto', flex: 'none' }}
              >
                Move to {nextStage.name}
              </button>
            ) : null}
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '64px 1fr auto',
              gap: 20,
              alignItems: 'start',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Swatch code={job.colour} size={64} />
              <span style={{ fontFamily: 'var(--font-data)', fontSize: 11 }}>{colour.code}</span>
              <SheenBars level={job.sheen} />
              {editable ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  title="Change the colour on this job"
                  onClick={() => st.openPicker('jobColour', null)}
                  style={{
                    height: 34,
                    padding: '0 6px',
                    fontSize: 10.5,
                    letterSpacing: '0.06em',
                    whiteSpace: 'nowrap',
                    marginTop: 2,
                  }}
                >
                  Change
                </button>
              ) : null}
            </div>

            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 26, margin: '0 0 4px' }}>{job.title}</h1>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-muted)' }}>
                {job.customer} · their ref {job.ref} · {colour.brand} {colour.name} · 2-pac PU{' '}
                {SHEENS[job.sheen].toLowerCase()}, {coatsFor(job.sheen)} coats
              </p>
              {seesCustomers(st) ? (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => st.openCustomer(job.customer)}
                  style={{ paddingInline: 0, marginTop: 4 }}
                >
                  All {job.customer} jobs →
                </button>
              ) : null}

              {/* Every fact the office types is a way into the form that types
                it — click the date, land on the date. Items is the exception
                that proves the rule: it is the sum of the item lines, so it
                opens the editor those are entered in rather than a field. */}
              {/* Room between the facts on a line, but not a line's worth of
                  it between two rows of them — a header that wraps should get
                  taller by a row, not by a band of nothing. */}
              <div
                style={{
                  display: 'flex',
                  columnGap: 30,
                  rowGap: 14,
                  marginTop: 16,
                  flexWrap: 'wrap',
                }}
              >
                <HeadFact
                  label="Received"
                  value={formatDay(job.received)}
                  mono
                  edit={edit('received')}
                />
                <HeadFact
                  label="Est. start"
                  value={formatDay(plan.estStart)}
                  mono
                  edit={edit('estStart')}
                />
                <HeadFact label="Est. days" value={`${plan.estDays}`} mono edit={edit('estDays')} />
                <HeadFact
                  label="Promised"
                  value={formatDay(job.promised)}
                  mono
                  urgent={overdue}
                  edit={edit('promised')}
                />
                <HeadFact
                  label="Deadline"
                  value={formatDay(plan.deadline)}
                  mono
                  urgent={risk}
                  edit={edit('deadline')}
                />
                <HeadFact label="Site" value={job.site} edit={edit('site')} />
                <HeadFact
                  label="Items"
                  value={`${job.items} · ${areaFor(job).toFixed(1)} m²`}
                  mono
                  edit={editable ? () => st.set('screen', 'items') : undefined}
                />
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                alignItems: 'stretch',
                minWidth: 210,
              }}
            >
              {/* No stage badge here. The track above says where the job is
                standing, with its date and its place in the run — a second
                copy of the same word beside the move button is one more thing
                to keep true and nothing more to read. */}
              {editable ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary blueprint"
                    onClick={() => st.set('moveOpen', true)}
                    style={{ height: 48 }}
                  >
                    Move to {nextStage.name}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => st.openJobEdit()}
                    style={{ height: 48 }}
                  >
                    Edit details
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => st.set('screen', 'items')}
                    style={{ height: 48 }}
                  >
                    Add items
                  </button>
                </>
              ) : null}

              {risk ? (
                <p
                  style={{
                    margin: 0,
                    fontSize: 12.5,
                    color: 'var(--urgent)',
                    lineHeight: 1.45,
                    border: '1px solid var(--urgent)',
                    borderRadius: 12,
                    padding: 10,
                  }}
                >
                  Planned finish runs past the {formatDay(plan.deadline)} deadline. Start earlier or
                  shorten the estimate.
                </p>
              ) : null}

              {!editable ? (
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: 'var(--ink-muted)',
                    lineHeight: 1.45,
                    borderLeft: '2px solid var(--line)',
                    paddingLeft: 10,
                  }}
                >
                  You can read this job but not change it — you&rsquo;re not on it. Ask the office
                  to put you on {job.code}.
                </p>
              ) : null}
            </div>
          </div>
        )}

        {/* The fold, astride the bottom edge of the header where it reads as
            the edge of the thing it folds. The job sheet is one screen and the
            lists inside it scroll, so the only way to give the items or the
            stage history more room is to take it from up here — and the shop
            asked for it back. */}
        <button
          type="button"
          className="edge-tab"
          aria-expanded={st.jobHeadOpen}
          onClick={() => st.set('jobHeadOpen', !st.jobHeadOpen)}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            style={{ transform: st.jobHeadOpen ? undefined : 'rotate(180deg)' }}
          >
            <path d="M1.5 6.5 5 3l3.5 3.5" />
          </svg>
          {st.jobHeadOpen ? 'Hide job details' : 'Show job details'}
        </button>
      </Panel>

      {/* `minmax(0,1fr)` on the row, not `auto`: an auto row is sized to its
          content and would grow straight past the bottom of the screen, which
          is the thing this layout exists to stop. The floor is the other half —
          on a screen too short to hold both, the page scrolls rather than
          squeezing the lists to a slot. */}
      <div
        style={{
          flex: 1,
          minHeight: 320,
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1.55fr) minmax(0,1fr)',
          gridTemplateRows: 'minmax(0,1fr)',
          gap: 20,
          alignItems: 'stretch',
        }}
      >
        {/* Items, tasks and photos take turns. Each tab carries its own count,
            so switching away hides the list and never the fact that it is
            there — nine tasks are nine tasks whether or not they are on screen.
            The list scrolls inside the panel; the tab bar and its one action
            stay where they were put. */}
        <Panel
          padding={0}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
        >
          <div
            style={{
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '12px 16px',
              borderBottom: '1px solid var(--line-soft)',
            }}
          >
            <Tabs
              label="Job sheet"
              tabIdFor={jobTabId}
              panelId={JOB_PANEL_ID}
              active={st.jobTab}
              onPick={(key) => st.set('jobTab', key as JobTab)}
              options={[
                { key: 'items', label: 'Items', meta: `${itemCount}` },
                { key: 'tasks', label: 'Tasks', meta: `${tasksDone}/${st.tasks.length}` },
                { key: 'photos', label: 'Photos', meta: `${photoCount}` },
              ]}
            />
            {roomForTabNote ? (
              <span
                style={{
                  fontFamily: 'var(--font-data)',
                  fontSize: 12,
                  color: 'var(--ink-muted)',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {tabMeta[st.jobTab]}
              </span>
            ) : null}
            {editable ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={tabAction[st.jobTab].run}
                style={{ marginLeft: 'auto', minHeight: 48, paddingInline: 12, flex: 'none' }}
              >
                {tabAction[st.jobTab].label}
              </button>
            ) : null}
          </div>

          <div
            role="tabpanel"
            id={JOB_PANEL_ID}
            aria-labelledby={jobTabId(st.jobTab)}
            tabIndex={0}
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '2px 18px 18px' }}
          >
            {st.jobTab === 'items' ? (
              st.rows.length === 0 ? (
                <Empty>
                  No item lines on {job.code} yet. Add the door schedule and the m² comes with it.
                </Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {st.rows.map((r, i) => (
                    <div
                      key={r.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '22px 30px 1fr auto',
                        gap: 12,
                        alignItems: 'center',
                        padding: '10px 2px',
                        borderTop: i === 0 ? 'none' : '1px solid var(--line-soft)',
                      }}
                    >
                      {/* The line number the floor quotes back — same one that
                          is on the item editor and on the job sheet. */}
                      <span
                        style={{
                          fontFamily: 'var(--font-data)',
                          fontSize: 11.5,
                          textAlign: 'right',
                          color: 'var(--ink-muted)',
                        }}
                      >
                        {r.no}
                      </span>
                      <Swatch code={r.colour} size={30} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          {r.qty} × {r.type}
                        </div>
                        <div
                          style={{
                            fontFamily: 'var(--font-data)',
                            fontSize: 11.5,
                            color: 'var(--ink-muted)',
                          }}
                        >
                          {r.w}×{r.h} · {r.substrate} · {r.faces === 2 ? '2 faces' : '1 face'} ·{' '}
                          {r.edge} · {r.colour} {r.finish.toLowerCase()} ×{r.coats}
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: 'right',
                          fontFamily: 'var(--font-data)',
                          fontSize: 12,
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{r.qty}</div>
                        <div style={{ color: 'var(--ink-muted)' }}>{sqmOf(r).toFixed(2)} m²</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : null}

            {st.jobTab === 'tasks' ? (
              st.tasks.length === 0 ? (
                <Empty>
                  Nothing on the checklist for {job.code}. Add the first task so the floor knows
                  what this job needs.
                </Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {st.tasks.map((t, i) => (
                    <button
                      key={t.label}
                      type="button"
                      onClick={() => st.toggleTask(i)}
                      aria-pressed={t.done}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        minHeight: 48,
                        padding: '0 2px',
                        borderTop: i === 0 ? 'none' : '1px solid var(--line-soft)',
                        background: 'transparent',
                        borderLeft: 0,
                        borderRight: 0,
                        borderBottom: 0,
                        textAlign: 'left',
                        cursor: editable ? 'pointer' : 'default',
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 7,
                          flex: 'none',
                          border: `1px solid ${t.done ? 'var(--accent)' : 'var(--dim)'}`,
                          background: t.done ? 'var(--accent)' : 'var(--surface)',
                          color: 'var(--on-accent)',
                          fontFamily: 'var(--font-data)',
                          fontSize: 13,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {t.done ? '×' : ''}
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          color: t.done ? 'var(--ink-muted)' : 'var(--ink)',
                          textDecoration: t.done ? 'line-through' : 'none',
                        }}
                      >
                        {t.label}
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-data)',
                          fontSize: 11,
                          color: 'var(--ink-muted)',
                          marginLeft: 'auto',
                        }}
                      >
                        {t.who}
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-data)',
                          fontSize: 11,
                          minWidth: 76,
                          textAlign: 'right',
                          color: t.due === 'Done' ? 'var(--ink-muted)' : 'var(--ink)',
                        }}
                      >
                        {t.due}
                      </span>
                    </button>
                  ))}
                </div>
              )
            ) : null}

            {st.jobTab === 'photos' ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: 12,
                  paddingTop: 14,
                }}
              >
                {[
                  'Goods in · 4',
                  `Damage · ${job.damaged}`,
                  'In progress · 3',
                  'Colour match · 2',
                ].map((label) => (
                  <figure
                    key={label}
                    className="blueprint"
                    style={{
                      margin: 0,
                      aspectRatio: '4/3',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      padding: 8,
                      borderRadius: 12,
                      overflow: 'hidden',
                      background:
                        'repeating-linear-gradient(135deg,var(--ground) 0 6px,var(--line-soft) 6px 12px)',
                    }}
                  >
                    <figcaption
                      style={{
                        fontFamily: 'var(--font-heading)',
                        fontSize: 9.5,
                        fontWeight: 600,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'var(--ink)',
                        background: 'var(--surface)',
                        padding: '3px 7px',
                        borderRadius: 999,
                        alignSelf: 'flex-start',
                        margin: 0,
                      }}
                    >
                      {label}
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : null}
          </div>
        </Panel>

        {/* The right rail scrolls on its own. Who is on the job and what has
            happened to it are read alongside whichever list is open, not by
            scrolling the whole sheet past them. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            minHeight: 0,
            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          <Panel>
            <PanelHead
              title="Who's on this job"
              meta={job.who.length ? `${job.who.length} with write access` : 'Nobody assigned'}
            />
            {job.who.length === 0 ? (
              <Empty>
                Nobody is on this job yet, so nobody on the floor can tick tasks or move it. Assign
                a sprayer to start it.
              </Empty>
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {job.who.map((w) => {
                const p = personFor(w);
                return (
                  <div key={w} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar initials={w} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-muted)' }}>{p.role}</div>
                    </div>
                    <span
                      style={{
                        fontFamily: 'var(--font-heading)',
                        fontSize: 9.5,
                        fontWeight: 600,
                        letterSpacing: '0.09em',
                        textTransform: 'uppercase',
                        padding: '3px 8px',
                        borderRadius: 999,
                        marginLeft: 'auto',
                        background: 'var(--accent-tint)',
                        color: 'var(--accent-deep)',
                      }}
                    >
                      On job
                    </span>
                  </div>
                );
              })}
            </div>
            {editable ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => st.flash('Assigning someone gives them write access to this job.')}
                style={{ height: 48, marginTop: 14 }}
              >
                Assign someone
              </button>
            ) : null}
          </Panel>

          <Panel>
            <h2
              style={{
                fontSize: 15,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                margin: '0 0 12px',
              }}
            >
              Goods in
            </h2>
            <table className="table" style={{ width: '100%', fontSize: 13 }}>
              <tbody>
                <GoodsRow label="Expected" value={`${job.items}`} />
                <GoodsRow label="Arrived" value={`${job.items}`} />
                <GoodsRow
                  label="Damaged"
                  value={job.damaged ? `${job.damaged} — photographed` : 'None'}
                  urgent={job.damaged > 0}
                />
              </tbody>
            </table>
          </Panel>

          <Panel>
            <h2
              style={{
                fontSize: 15,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                margin: '0 0 12px',
              }}
            >
              Notes
            </h2>
            {editable ? (
              <>
                <textarea
                  className="input"
                  value={st.note}
                  autoComplete={NO_AUTOFILL}
                  onChange={(e) => st.setNote(e.target.value)}
                  placeholder="Add a note for the shop floor"
                  style={{ width: '100%', minHeight: 88, fontSize: 16, resize: 'vertical' }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={st.saveNote}
                  style={{ height: 48, marginTop: 10, width: '100%' }}
                >
                  Save note
                </button>
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
                {st.note || 'No notes on this job yet. Anyone on the job can add one.'}
              </p>
            )}
          </Panel>

          <Panel>
            <h2
              style={{
                fontSize: 15,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                margin: '0 0 12px',
              }}
            >
              Stage history
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {events.map((h) => (
                <div key={h.when + h.what} style={{ display: 'flex', gap: 10, fontSize: 13 }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: 11.5,
                      color: 'var(--ink-muted)',
                      minWidth: 78,
                    }}
                  >
                    {formatInstant(h.when)}
                  </span>
                  <span style={{ flex: 1 }}>{h.what}</span>
                  <span style={{ color: 'var(--ink-muted)' }}>{h.who}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </section>
  );
}

interface TrackStep {
  id: string;
  name: string;
  /** The stage's own board colour — the one thing on the track that isn't grey. */
  tone: string;
  state: 'done' | 'now' | 'ahead';
  /** The day the job reached it, on the stages it has reached. */
  when: string | null;
}

/** Where the job is up to, read left to right, with every stage on it.
 *
 *  State is carried by shape and by a word, never by colour alone: a stage the
 *  job is through is a filled tick with the day it got there, the stage it is
 *  standing at is that stage's own board colour ringed in ink and captioned
 *  Now, and what is ahead is hollow and quiet. A long stage list scrolls
 *  sideways rather than squeezing a name past reading. It rides in the job
 *  header's top band beside the job code, so the caption naming it lives out
 *  there with the code rather than in here. */
function StageTrack({ steps, note }: { steps: TrackStep[]; note: string | null }) {
  const at = steps.findIndex((s) => s.state === 'now');

  return (
    <div>
      <ol
        aria-label="Stage progress"
        style={{
          display: 'flex',
          listStyle: 'none',
          margin: 0,
          padding: '0 0 2px',
          overflowX: 'auto',
        }}
      >
        {steps.map((s, i) => {
          const done = s.state === 'done';
          const now = s.state === 'now';
          /* The rail into a stage fills once the job has reached it; the rail
             out of it only once the job has left. */
          const into = i <= at ? 'var(--accent)' : 'var(--line)';
          const outOf = i < at ? 'var(--accent)' : 'var(--line)';

          return (
            <li
              key={s.id}
              aria-current={now ? 'step' : undefined}
              style={{
                position: 'relative',
                flex: '1 1 0',
                minWidth: 96,
                padding: '0 4px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              {i > 0 ? (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 0,
                    width: '50%',
                    height: 2,
                    background: into,
                  }}
                />
              ) : null}
              {i < steps.length - 1 ? (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: '50%',
                    right: 0,
                    height: 2,
                    background: outOf,
                  }}
                />
              ) : null}

              <span
                aria-hidden
                style={{
                  position: 'relative',
                  zIndex: 1,
                  flex: 'none',
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  lineHeight: 1,
                  color: 'var(--on-accent)',
                  background: done
                    ? 'var(--accent)'
                    : now
                      ? s.tone || 'var(--surface)'
                      : 'var(--surface)',
                  border: `2px solid ${done ? 'var(--accent)' : now ? 'var(--ink)' : 'var(--dim)'}`,
                  boxShadow: now
                    ? '0 0 0 3px color-mix(in srgb, var(--ink) 12%, transparent)'
                    : undefined,
                }}
              >
                {done ? '✓' : ''}
              </span>

              <span
                className="label-xs"
                style={{
                  marginTop: 8,
                  fontSize: 10.5,
                  letterSpacing: '0.09em',
                  lineHeight: 1.25,
                  fontWeight: now ? 700 : 600,
                  color: done || now ? 'var(--ink)' : 'var(--ink-muted)',
                }}
              >
                {s.name}
              </span>

              <span
                style={{
                  marginTop: 4,
                  minHeight: 14,
                  fontFamily: 'var(--font-data)',
                  fontSize: 10.5,
                  fontWeight: now ? 600 : 400,
                  color: now ? 'var(--ink)' : 'var(--ink-muted)',
                }}
              >
                {now ? 'Now' : (s.when ?? '')}
              </span>
            </li>
          );
        })}
      </ol>

      {note ? (
        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--ink-muted)' }}>{note}</p>
      ) : null}
    </div>
  );
}

/** One fact off the job header — and, for anyone who may edit the job, the way
 *  into changing it.
 *
 *  Clicking the fact opens the job's form on that field, which is the same
 *  form Edit details opens. It is a pointer-device shortcut on top of that
 *  full-size button, the way dragging a card is on top of the move sheet: the
 *  48px target is the button, and nothing here is the only way to reach a
 *  field. Read-only renders the same text with nothing to press — calm, not a
 *  dead control. */
function HeadFact({
  label,
  value,
  mono,
  urgent,
  edit,
}: {
  label: string;
  value: string;
  mono?: boolean;
  urgent?: boolean;
  edit?: () => void;
}) {
  const body = (
    <>
      <div className="label-xs" style={{ marginBottom: 3, letterSpacing: '0.12em' }}>
        {label}
      </div>
      <div
        className="fact-value"
        style={{
          fontFamily: mono ? 'var(--font-data)' : undefined,
          fontSize: 14,
          fontWeight: urgent ? 500 : undefined,
          color: urgent ? 'var(--urgent)' : undefined,
        }}
      >
        {value}
      </div>
    </>
  );

  return edit ? (
    <button type="button" className="fact-edit" onClick={edit} title={`Change ${label}`}>
      {body}
    </button>
  ) : (
    <div>{body}</div>
  );
}

function GoodsRow({ label, value, urgent }: { label: string; value: string; urgent?: boolean }) {
  return (
    <tr>
      <td style={{ color: 'var(--ink-muted)' }}>{label}</td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>
        <span style={{ fontSize: 13, color: urgent ? 'var(--urgent)' : 'var(--ink)' }}>
          {value}
        </span>
      </td>
    </tr>
  );
}
